import { useEffect, useState, useMemo, useRef, useCallback } from "react"
import { useStorePads } from "../store"
import useMidiMapping from "../hooks/useMidiMapping"

const noteToMidiNumber = (noteName) => {
    const notes = {
        'C': 0,
        'C#': 1,
        'D': 2,
        'D#': 3,
        'E': 4,
        'F': 5,
        'F#': 6,
        'G': 7,
        'G#': 8,
        'A': 9,
        'A#': 10,
        'B': 11
    }

    if (!noteName) return null
    const normalized = String(noteName).trim().toUpperCase()
    return notes[normalized] ?? null
}

export default function ButtonPad({ note, player }) {
    const currentLibraryName = useStorePads((state) => state.currentLibrary?.name)
    const playback = useStorePads((state) => state.playback)
    const setPlayback = useStorePads((state) => state.setPlayback)
    const setMidiAssignment = useStorePads((state) => state.setMidiAssignment)
    const removeMidiAssignment = useStorePads((state) => state.removeMidiAssignment)

    const { initMidi, isMidiReady, registerMidiTarget, unregisterMidiTarget, startMidiLearning, stopMidiLearning, getNoteName, getAssignedMidiAssignment, applyMidiAssignmentToTarget } = useMidiMapping()

    const [assignedMapping, setAssignedMapping] = useState(null)
    const [isMapping, setIsMapping] = useState(false)
    const [isContextVisible, setIsContextVisible] = useState(false)
    const [contextPosition, setContextPosition] = useState({ x: 0, y: 0 })

    const activeValue = playback?.library === currentLibraryName && playback?.note === note
    const isFlat = String(note).includes("#")
    const loading = false

    const targetId = useMemo(() => `pad-button-${note}`.replace(/\s+/g, '-').toLowerCase(), [note])
    const defaultNote = useMemo(() => noteToMidiNumber(note), [note])

    const baseBgClass = activeValue
        ? 'bg-indigo-500'
        : (isFlat ? 'bg-neutral-950' : 'bg-neutral-950 bg-opacity-60')

    useEffect(() => {
        initMidi()
    }, [initMidi])

    useEffect(() => {
        if (!targetId) return

        const existing = getAssignedMidiAssignment(targetId)
        if (existing) {
            setAssignedMapping(existing)
        } else if (defaultNote !== null && defaultNote !== undefined) {
            const defaultAssignment = { type: 'note', number: defaultNote }
            applyMidiAssignmentToTarget(targetId, defaultAssignment)
            setAssignedMapping(defaultAssignment)
            setMidiAssignment(targetId, defaultAssignment)
        }
    }, [targetId, getAssignedMidiAssignment, defaultNote, applyMidiAssignmentToTarget, setMidiAssignment])

    const toggleMapping = () => {
        if (!isMidiReady) return
        setIsMapping((prev) => !prev)
    }

    const closeContext = () => {
        setIsContextVisible(false)
    }

    const currentMappingLabel = assignedMapping
        ? getNoteName(assignedMapping.number)
        : 'No asignado'

    const handlePlay = useCallback(() => {
        setPlayback(activeValue ? null : {
            source: 'pad',
            library: currentLibraryName,
            note
        })
    }, [activeValue, currentLibraryName, note, setPlayback])

    const wrapperRef = useRef(null)
    const buttonRef = useRef(null)
    const longPressTimerRef = useRef(null)
    const longPressTriggeredRef = useRef(false)
    const touchStartRef = useRef({ x: 0, y: 0 })

    useEffect(() => {
        if (!targetId) return

        const assignedNote = assignedMapping?.number ?? defaultNote

        const handleMidiMessage = (message) => {
            if (message?.type !== 'note') return
            if (assignedNote == null || message.number !== assignedNote) return

            const isNoteOn = message.value > 0
            if (!isNoteOn) return

            buttonRef.current?.click()
        }

        const handleMidiAssigned = (assignment) => {
            setAssignedMapping(assignment)
            setIsMapping(false)
            if (assignment) {
                setMidiAssignment(targetId, assignment)
            } else {
                removeMidiAssignment(targetId)
            }
        }

        registerMidiTarget(targetId, {
            onMidiMessage: handleMidiMessage,
            onMidiAssigned: handleMidiAssigned
        })

        const existingAssignment = getAssignedMidiAssignment(targetId)
        if (existingAssignment) {
            applyMidiAssignmentToTarget(targetId, existingAssignment)
        }

        return () => {
            unregisterMidiTarget(targetId)
        }
    }, [targetId, registerMidiTarget, unregisterMidiTarget, getAssignedMidiAssignment, applyMidiAssignmentToTarget, setMidiAssignment, removeMidiAssignment, assignedMapping?.number, defaultNote])

    useEffect(() => {
        if (isMapping && targetId) {
            startMidiLearning(targetId)
            return () => stopMidiLearning()
        }

        stopMidiLearning()
    }, [isMapping, targetId, startMidiLearning, stopMidiLearning])

    useEffect(() => {
        return () => {
            if (longPressTimerRef.current) {
                clearTimeout(longPressTimerRef.current)
            }
        }
    }, [])

    const openContextMenu = useCallback((x, y) => {
        setContextPosition({ x, y })
        setIsContextVisible(true)
    }, [])

    const clearLongPress = useCallback(() => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current)
            longPressTimerRef.current = null
        }
    }, [])

    const handleTouchStart = useCallback((event) => {
        const touch = event.touches?.[0]
        if (!touch) return

        longPressTriggeredRef.current = false
        touchStartRef.current = { x: touch.clientX, y: touch.clientY }
        clearLongPress()
        longPressTimerRef.current = setTimeout(() => {
            longPressTriggeredRef.current = true
            openContextMenu(touch.clientX, touch.clientY)
        }, 550)
    }, [clearLongPress, openContextMenu])

    const handleTouchMove = useCallback((event) => {
        const touch = event.touches?.[0]
        if (!touch) return

        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x)
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y)
        if (deltaX > 10 || deltaY > 10) {
            clearLongPress()
        }
    }, [clearLongPress])

    const handleTouchEnd = useCallback((event) => {
        if (longPressTriggeredRef.current) {
            event.preventDefault()
        }
        clearLongPress()
    }, [clearLongPress])

    return (
        <div
            ref={wrapperRef}
            className="relative"
            onContextMenu={(event) => {
                event.preventDefault()
                openContextMenu(event.clientX, event.clientY)
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={clearLongPress}
        >
            <button
                type="button"
                ref={buttonRef}
                onClick={handlePlay}
                className={`relative w-full h-full ${baseBgClass} overflow-hidden border border-gray-700 rounded-lg focus:outline-none`}
                title={note}
            >
                <div className='w-full h-full flex items-center justify-center flex-col'>
                    <span className="text-4xl font-medium">
                        {note}
                    </span>
                    <span className="text-xs text-white/70 mt-1">{currentMappingLabel}</span>
                    {
                        loading && (
                            <div className="w-4 h-4 border-2 border-l-transparent border-gray-800 animate-spin rounded-full" />
                        )
                    }
                </div>
            </button>

            {isContextVisible && (
                <div
                    className="fixed z-50 bg-neutral-900 border border-gray-700 rounded shadow-lg p-3 min-w-[200px]"
                    style={{ top: contextPosition.y, left: contextPosition.x }}
                >
                    <button
                        type="button"
                        className="w-full text-left px-2 py-1 mb-2 rounded bg-slate-800 hover:bg-slate-700"
                        onClick={toggleMapping}
                    >
                        {isMapping ? 'Detener asignación MIDI' : 'Asignar nota MIDI'}
                    </button>
                    <button
                        type="button"
                        className="w-full text-left px-2 py-1 rounded bg-slate-800 hover:bg-slate-700"
                        onClick={() => {
                            setAssignedMapping(null)
                            removeMidiAssignment(targetId)
                            setIsMapping(false)
                            closeContext()
                        }}
                    >
                        Quitar asignación MIDI
                    </button>
                </div>
            )}

            {isContextVisible && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={closeContext}
                />
            )}
        </div>
    )
}
